import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { Policy } from '../../../../../shared/models/info/policy';
import { UnsavedChangesTrackerService } from '../../../../services/unsaved-changes-tracker.service';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { Project } from '../../../../../shared/models/project';
import { ProjectApiService } from '../../../../services/api/project-api.service';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-edit-project',
  imports: [FormsModule, ReactiveFormsModule, TextAreaComponent, FloppyIconComponent, AgentActionDirective],
  templateUrl: './edit-project.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-project.component.scss',
})
export class EditProjectComponent implements OnInit, OnChanges {
  @Input() public project?: Project;
  @Output() public updatedProject = new EventEmitter<Project>();

  public projectForm!: FormGroup;
  public internalNotes!: FormControl;
  public productLine!: FormControl;
  public shortDescription!: FormControl;

  public editDisabled = true;

  private projectApiService = inject(ProjectApiService);
  private toast = inject(HotToastService);
  private accessRightsService = inject(AccessRightsService);
  private unsavedChangesService = inject(UnsavedChangesTrackerService);
  private appInsightsService = inject(AppInsightsService);

  public ngOnInit(): void {
    this.init();
    this.appInsightsService.logSidebarUsage('Project_edit');
  }

  public ngOnChanges(): void {
    this.init();
  }

  public update(): void {
    this.updateValuesFromForm();
    this.projectApiService.updateProject(this.project!).subscribe({
      next: (updatedProject) => {
        this.updatedProject.emit(updatedProject);
        this.toast.success('Project updated successfully');
        this.projectForm.markAsPristine();
        this.project!.rowVersion = updatedProject.rowVersion;
      },
      error: () => {
        console.error('Error updating SW-Part-Number');
      },
    });
  }

  private init(): void {
    this.initForm();
    this.accessRightsService.getAccessRights$().subscribe((accessRights) => {
      this.editDisabled = !accessRights.authorized(Policy.canUpdateBasic);
    });
    this.setFormValues();
    this.projectForm.valueChanges.subscribe(() => {
      this.saveUnsavedChangesToSessionStorage();
    });
  }

  private initForm(): void {
    this.internalNotes = new FormControl(null, []);
    this.productLine = new FormControl(null, []);
    this.shortDescription = new FormControl(null, []);
    this.projectForm = new FormGroup({
      internalNotes: this.internalNotes,
      productLine: this.productLine,
      shortDescription: this.shortDescription,
    });
  }

  private setFormValues(): void {
    if (this.project) {
      this.projectForm.patchValue({
        internalNotes: this.project.internalNotes,
        productLine: this.project.productLine,
        shortDescription: this.project.shortDescription,
      });
    }
  }

  private updateValuesFromForm(): void {
    if (this.project) {
      this.project.internalNotes = this.projectForm.value.internalNotes;
      this.project.productLine = this.projectForm.value.productLine;
      this.project.shortDescription = this.projectForm.value.shortDescription;
    }
  }

  private saveUnsavedChangesToSessionStorage(): void {
    if (this.projectForm) {
      this.updateValuesFromForm();
      this.unsavedChangesService.saveUnsavedChangesToSessionStorage(STORAGE_KEYS.UNSAVED_PROJECT, this.project);
    }
  }
}
